/**
 * GSAP inside React.
 *
 * Every timeline is created in a context and reverted on unmount, so an
 * animation never outlives the component that owns it. Respecting
 * prefers-reduced-motion is built in rather than left to each call site: the
 * survey is answered on phones by people who may need it.
 */
import { useEffect, useRef, type DependencyList, type RefObject } from 'react';
import gsap from 'gsap';

export const prefersReducedMotion = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Run `setup` inside a gsap.context scoped to `scope`, re-running when `deps`
 * change. The context is reverted on cleanup, which kills every tween and
 * ScrollTrigger it created and restores inline styles.
 */
export const useGsap = (
  scope: RefObject<HTMLElement | null>,
  setup: (context: gsap.Context) => void,
  deps: DependencyList = [],
): void => {
  useEffect(() => {
    if (!scope.current || prefersReducedMotion()) return;

    const context = gsap.context((self) => setup(self), scope.current);
    return () => context.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

/** Fade-and-rise entrance, matching the prototypes' `rise` keyframe. */
export const useRiseIn = (
  scope: RefObject<HTMLElement | null>,
  selector: string,
  deps: DependencyList = [],
): void => {
  useGsap(
    scope,
    () => {
      gsap.from(selector, {
        opacity: 0,
        y: 10,
        duration: 0.4,
        ease: 'power2.out',
        stagger: 0.04,
      });
    },
    deps,
  );
};

/** Imperative handle for one-off tweens that need their own cleanup. */
export const useTimeline = () => {
  const timeline = useRef<gsap.core.Timeline | null>(null);
  useEffect(() => () => void timeline.current?.kill(), []);
  return timeline;
};
